import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type MultiSelectOption = {
  value: string;
  label: string;
  isSelected: boolean;
};

export interface MultiSelectPopoverProps {
  summary: string;
  options: MultiSelectOption[];
  onSelect: (value: string) => void;
  searchPlaceholder: string;
  emptyMessage: string;
  loadingMessage?: string;
  isLoading?: boolean;
}

export function MultiSelectPopover({
  summary,
  options,
  onSelect,
  searchPlaceholder,
  emptyMessage,
  loadingMessage = 'Se încarcă...',
  isLoading = false,
}: MultiSelectPopoverProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between sm:w-64">
          <div className="flex items-center">
            <Plus className="mr-2 h-4 w-4" />
            {summary}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0 sm:w-64">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="max-h-64 overflow-y-auto">
            <CommandEmpty>
              {isLoading ? loadingMessage : emptyMessage}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => onSelect(option.value)}>
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      option.isSelected ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
